import {FC, useEffect, useMemo, useState} from "react";
import {Stack} from "@mui/material";
import {ProgressWidget, ProgressWidgetSkeleton} from "./ProgressWidget";
import {useTeamProgressContext, SelectedTile} from "../../context/TeamProgressContext";
import {useTiles} from "../../hooks";
import {useSSTranslation} from "@/i18n/useSSTranslation";

interface IWidget {
	key: SelectedTile;
	value: string;
	isRedFlag: boolean;
	underConstruction: boolean;
}

/**
 * Widget List Component
 * 
 * Matches legacy WidgetList component exactly:
 * - Horizontal wrap layout (2 rows of 3 widgets)
 * - Red flag styling for inactive/overdue
 * - Under construction support
 * - Loading skeletons
 */
export function WidgetList() {
	const {selectedTile, setSelectedTile, allSectionsLoaded, updateSectionState, sectionsLoaded} = useTeamProgressContext();
	const {tiles, isLoading} = useTiles();
	const {t} = useSSTranslation();
	const [enabledCard, setEnabledCard] = useState<boolean>(false);

	useEffect(() => {
		updateSectionState(isLoading, "widgets");
	}, [isLoading, updateSectionState]);

	useEffect(() => {
		const sectionsLoadedState = allSectionsLoaded();
		setEnabledCard(sectionsLoadedState);
	}, [sectionsLoaded, allSectionsLoaded]);

	const widgets = useMemo(() => {
		if (!tiles) return [];

		const result: IWidget[] = [];
		const redFlagWidgets: SelectedTile[] = ["inactiveUsers", "usersWithOverdueLessons"];
		const underConstructionWidgets: SelectedTile[] = [];

		for (const [attribute, value] of Object.entries(tiles)) {
			const widget: IWidget = {
				key: attribute as SelectedTile,
				value: value as string,
				isRedFlag: redFlagWidgets.includes(attribute as SelectedTile),
				underConstruction: underConstructionWidgets.includes(attribute as SelectedTile)
			};
			result.push(widget);
		}

		return result;
	}, [tiles]);

	const handleUpdateSelectedWidget = (tileKey: string) => {
		if (enabledCard) {
			setSelectedTile(tileKey as SelectedTile);
		}
	};

	return (
		<Stack
			direction="row"
			gap={2}
			sx={{flexWrap: "wrap"}}
		>
			{
				!isLoading &&
				widgets.map(widget => {
					const selected = selectedTile === widget.key;

					return (
						<ProgressWidget
							key={widget.key}
							widget={widget}
							isSelected={selected}
							onSelectWidget={handleUpdateSelectedWidget}
						/>
					);
				})
			}
			{
				isLoading &&
				Array.from({length: 6}, (_, index) =>
					<ProgressWidgetSkeleton key={`skeleton-${index}`} />
				)
			}
		</Stack>
	);
}
